import {
  Box,
  Button,
  Grid,
  Typography,
  makeStyles,
  Theme,
  createStyles,
  CircularProgress,
} from '@material-ui/core';
import React from 'react';
import ScheduleCalendar from '../ScheduleCalendar';
import mainColors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { getAvailableTimeSlotsForConsultation } from '../../queries';
import useAuthQuery from '../../../../hooks/useAuthQuery';
import {
  EnergyConsultationDayAvailability,
  EnergyConsultationDayAvailabilityResponse,
  PreferredLanguage,
  QueryGetEnergyConsultationDayAvailabilityArgs,
} from '../../../../__generated__/pge-types';
import format from 'date-fns/format';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateTimeTitle: {
      fontSize: 16,
      fontWeight: 700,
      marginBottom: theme.spacing(1.5),
    },
    calendarCard: {
      border: `1px solid ${mainColors.lightGray}`,
      borderRadius: 8,
      backgroundColor: mainColors.lightGray6,
      maxWidth: 450,
      overflow: 'hidden',
      '& .MuiPickersDay-daySelected': {
        backgroundColor: mainColors.shoreGreen,
        color: mainColors.white,
      },

      '& .MuiPickersDay-day.MuiPickersDay-dayDisabled': {
        color: 'rgba(0, 0, 0, 0.15)',
        '& p': {
          fontWeight: 'normal',
        },
      },
      '& .MuiPickersCalendarHeader-iconButton': {
        padding: theme.spacing(1),
        margin: theme.spacing(0, 1),
        background: 'transparent',
      },
    },
    timeSection: {
      paddingLeft: theme.spacing(1),
    },
    timeTitle: {
      fontWeight: 700,
      fontSize: 16,
      marginBottom: theme.spacing(1.5),
    },
    timeGrid: {
      display: 'flex',
      gap: `${theme.spacing(1)}px`,
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    timeButton: {
      border: `1px solid ${mainColors.lightGray8}`,
      borderRadius: 6,
      backgroundColor: mainColors.white,
      textTransform: 'none',
      minHeight: 42,
      fontSize: 14,
      width: 86,
      '&:hover': {
        borderColor: mainColors.shoreGreen,
        backgroundColor: mainColors.linenWhite,
      },
    },
    selectedTimeButton: {
      backgroundColor: mainColors.shoreGreen,
      color: mainColors.white,
      borderColor: mainColors.shoreGreen,
      '&:hover': {
        backgroundColor: mainColors.shoreGreen,
      },
    },
  }),
);

interface Props {
  isModal?: boolean;
  preferredLanguage?: PreferredLanguage | null;
  availableDays: EnergyConsultationDayAvailability[];
  date: Date | null;
  onDateChange: (d: Date) => void;
  time: string | undefined;
  onTimeChange: (t: string) => void;
  postalCode: string;
}

const ConsultationScheduler: React.FC<Props> = ({
  isModal = false,
  postalCode,
  preferredLanguage,
  availableDays,
  date,
  onDateChange,
  time,
  onTimeChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: availableSlotssData, loading: loadingSlots } = useAuthQuery<
    {
      getEnergyConsultationDayAvailability: EnergyConsultationDayAvailabilityResponse;
    },
    QueryGetEnergyConsultationDayAvailabilityArgs
  >(getAvailableTimeSlotsForConsultation, {
    variables: {
      input: {
        preferredLanguage,
        energyConsultationDate: date ? format(date, 'yyyy-MM-dd') : '', //2026-08-25
        postalCode,
      },
    },
    skip: !preferredLanguage || !date || !postalCode,
  });

  const availableTimeslots =
    availableSlotssData?.getEnergyConsultationDayAvailability?.slots;

  return (
    <>
      <Typography className={classes.dateTimeTitle}>
        {t('SELECT_PREFERRED_DATE_AND_TIME')}
      </Typography>

      {(!preferredLanguage || !postalCode) && (
        <Box mb={2}>
          <Alert severity="warning">
            {t('SELECT_LANGUAGE_TO_LOAD_AVAILABILITY')}
          </Alert>
        </Box>
      )}

      <Grid container spacing={3} data-testid="consultation-scheduler">
        <Grid item xs={12} md={isModal ? 12 : 6}>
          <Box className={classes.calendarCard}>
            <ScheduleCalendar
              onChange={e => {
                if (e) {
                  onDateChange(e);
                  onTimeChange('');
                }
              }}
              selectedDate={date || null}
              availableDates={
                availableDays
                  ?.filter(d => d.hasAvailability)
                  ?.map(d => d.date) || []
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={isModal ? 12 : 6}>
          <Box className={classes.timeSection}>
            <Typography className={classes.timeTitle}>
              {t('AVAILABLE_TIMES')}
            </Typography>
            <Box className={classes.timeGrid}>
              {loadingSlots && (
                <Box
                  css={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {!loadingSlots && availableTimeslots?.length === 0 && date && (
                <Alert severity="info">{t('TIMESLOTS_NOT_AVAILABLE')}</Alert>
              )}
              {!date && (
                <Alert severity="info">
                  {t('SELECT_DATE_TO_LOAD_TIMESLOTS')}
                </Alert>
              )}
              {!loadingSlots &&
                availableTimeslots?.map(availableTime => {
                  const isSelected =
                    time === availableTime.slotTime && availableTime.available;

                  return (
                    <Button
                      key={availableTime.slotTime}
                      className={`${classes.timeButton} ${
                        isSelected ? classes.selectedTimeButton : ''
                      }`}
                      onClick={() => onTimeChange(availableTime?.slotTime)}
                      disabled={!date || !availableTime?.available}
                    >
                      {availableTime?.slotTime}
                    </Button>
                  );
                })}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ConsultationScheduler;
